import { useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { AppShell } from '@/components/AppShell';
import { NoteCard } from '@/components/note';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useState } from 'react';

export function NotificationsPage() {
  useSeoMeta({ title: 'Notifications — Jumble', description: 'Mentions, replies, reactions and reposts.' });
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const [tab, setTab] = useState('all');

  const query = useQuery({
    queryKey: ['jumble', 'notifications', user?.pubkey],
    enabled: !!user,
    queryFn: async ({ signal }) => {
      const all = (await nostr.query(
        [{ kinds: [1, 6, 7, 9735], '#p': [user!.pubkey], limit: 100 }],
        { signal: signal ?? AbortSignal.timeout(5000) },
      )) as NostrEvent[];
      return all.sort((a, b) => b.created_at - a.created_at);
    },
    staleTime: 20_000,
  });

  const grouped = useMemo(() => {
    const all = query.data ?? [];
    return {
      all,
      mentions: all.filter((e) => e.kind === 1),
      reactions: all.filter((e) => e.kind === 7),
      reposts: all.filter((e) => e.kind === 6),
      zaps: all.filter((e) => e.kind === 9735),
    };
  }, [query.data]);

  if (!user) {
    return <AppShell title="Notifications"><p className="p-8 text-center text-sm text-muted-foreground">Log in to see mentions, replies and reactions.</p></AppShell>;
  }

  const list = tab === 'all' ? grouped.all : tab === 'mentions' ? grouped.mentions : tab === 'reactions' ? grouped.reactions : tab === 'reposts' ? grouped.reposts : grouped.zaps;

  return (
    <AppShell title="Notifications">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start overflow-x-auto rounded-none border-b bg-transparent">
          <TabsTrigger value="all">All ({grouped.all.length})</TabsTrigger>
          <TabsTrigger value="mentions">Replies</TabsTrigger>
          <TabsTrigger value="reactions">Reactions</TabsTrigger>
          <TabsTrigger value="reposts">Reposts</TabsTrigger>
          <TabsTrigger value="zaps">Zaps</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-0">
          {query.isLoading ? <div className="p-4"><Skeleton className="h-20 w-full" /></div>
            : list.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">Nothing here yet.</p>
            : list.map((e) => <NotificationRow key={e.id} event={e} />)}
        </TabsContent>
      </Tabs>
      <div className="pb-20 md:pb-4" />
    </AppShell>
  );
}

function NotificationRow({ event }: { event: NostrEvent }) {
  if (event.kind === 1) return <NoteCard event={event} />;
  if (event.kind === 9735) {
    return (
      <div className="border-b px-4 py-3 text-sm">
        <span className="font-semibold">⚡ Zap receipt</span>
        <span className="text-muted-foreground"> — someone zapped you. View details in a full client.</span>
      </div>
    );
  }
  const label = event.kind === 6 ? 'reposted' : `reacted ${event.content || '❤'}`;
  const target = event.tags.find((t) => t[0] === 'e')?.[1];
  return (
    <div className="border-b px-4 py-3 text-sm">
      <span className="font-semibold">Someone {label}</span>{' '}
      {target && <span className="font-mono text-xs text-muted-foreground">{target.slice(0, 12)}…</span>}
    </div>
  );
}
